import * as API from './api'
import { localFetch } from './local_fetch'

declare var Routes: any

export type TaxInformationStatus = 'loading' | 'loaded' | 'failed'

export class TaxInformation {
  // Stands in when a failure carries no server-supplied message (network drop,
  // non-JSON error page), so the rail always has something honest to show.
  public static readonly UNAVAILABLE_ERROR =
    'We could not calculate your order total. Please refresh and try again.'

  public static async calculateSalesTax(
    { address, amount }: { address: API.IAddress, amount: number },
  ): Promise<TaxInformation> {
    const payload = this.buildRequestPayload({ address, amount })

    if (!payload) {
      return TaxInformation.no_tax
    }

    const response = await localFetch(Routes.igniter_sales_tax_calculations_path(), {
      credentials: 'include',
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const json: API.ITaxInformation = await response.json()

    return new TaxInformation(json)
  }

  // Trial-redesign path: post the plan identifier (not a pre-computed amount) so
  // the server returns the legacy tax fields PLUS `totals` for both periods.
  public static async calculateOrderTotals(
    { address, planCode, monthly, trial }: {
      address: API.IAddress,
      planCode: string,
      monthly: boolean,
      trial: boolean,
    },
  ): Promise<TaxInformation> {
    const payload = this.buildOrderTotalsPayload({ address, planCode, monthly, trial })

    if (!payload) {
      return TaxInformation.no_tax
    }

    // A rejected promise here would leave `taxInformation` on `placeholder`, and
    // the rail stuck on "Calculating…" with no recovery. Every failure resolves
    // to the `failed` state instead, which the rail renders as terminal — never
    // as a fabricated $0.00, since this client computes no money of its own.
    try {
      const response = await localFetch(Routes.igniter_sales_tax_calculations_path(), {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        return TaxInformation.failure(await this.errorsFromResponse(response))
      }

      const json: API.ITaxInformation = await response.json()

      // A 200 can still carry `errors`: the tax calculator rescues its own
      // failures and answers 0.00 tax, so the totals built on top of it are
      // understated. That figure is not authoritative — fail rather than let the
      // rail present it as settled and then charge the real taxed amount.
      if (json.errors && json.errors.length > 0) {
        return TaxInformation.failure(json.errors)
      }

      return new TaxInformation(json)
    } catch {
      return TaxInformation.failure([])
    }
  }

  // The endpoint answers a 422 with the same `errors` array as a 200, so an
  // error body is the server's own explanation and outranks our generic copy.
  // Anything unparseable (a Rails error page) falls back to that copy.
  private static async errorsFromResponse(response: Response): Promise<string[]> {
    try {
      const json = await response.json()

      return Array.isArray(json?.errors) ? json.errors : []
    } catch {
      return []
    }
  }

  public static failure(errors: string[]): TaxInformation {
    return new TaxInformation(
      {
        sales_tax_amount: null,
        sales_tax_label: 'Sales Tax',
        errors: errors.length > 0 ? errors : [TaxInformation.UNAVAILABLE_ERROR],
      },
      'failed',
    )
  }

  // Re-project a loaded totals payload onto a purchase option without another
  // round-trip. The legacy fields track the selected option (Timeline/Step 3
  // read them), while `totals` carries both options for the Order Details rail.
  public static fromOrderTotals(
    totals: API.IOrderTotals,
    name: API.PurchaseOptionNameType,
  ): TaxInformation {
    const optionTotals = totals[name]

    if (!optionTotals) {
      return TaxInformation.no_tax
    }

    return new TaxInformation({
      sales_tax_amount: optionTotals.tax,
      sales_tax_label: optionTotals.tax_label,
      errors: [],
      totals,
    })
  }

  public static get no_tax(): TaxInformation {
    return new TaxInformation({
      sales_tax_amount: 0.00,
      sales_tax_label: 'Sales Tax',
      errors: [],
    })
  }

  public static get placeholder(): TaxInformation {
    return new TaxInformation(
      {
        sales_tax_amount: 0.00,
        sales_tax_label: 'Sales Tax',
        errors: [],
      },
      'loading',
    )
  }

  private static buildRequestPayload(
    { address, amount }: { address: API.IAddress, amount: number },
  ): API.ITaxAPIRequest | null {
    const {
      address1,
      address2,
      city,
      state,
      province,
      zip,
      country,
    } = address

    const apiState = state || province

    if (!apiState || !zip) {
      return null
    }

    return {
      amount,
      address: {
        street: [ address1, address2 ].join(' '),
        city,
        state: apiState,
        zip,
        country,
      },
    }
  }

  private static buildOrderTotalsPayload(
    { address, planCode, monthly, trial }: {
      address: API.IAddress,
      planCode: string,
      monthly: boolean,
      trial: boolean,
    },
  ): API.IOrderTotalsAPIRequest | null {
    const {
      address1,
      address2,
      city,
      state,
      province,
      zip,
      country,
    } = address

    const apiState = state || province

    if (!apiState || !zip) {
      return null
    }

    return {
      plan_code: planCode,
      monthly,
      trial,
      address: {
        street: [ address1, address2 ].join(' '),
        city,
        state: apiState,
        zip,
        country,
      },
    }
  }

  public salesTaxAmount: number | null
  public salesTaxLabel: string
  public errors: string[]
  public status: TaxInformationStatus
  public totals: API.IOrderTotals | null

  public constructor(api: API.ITaxInformation, status: TaxInformationStatus = 'loaded') {
    this.salesTaxAmount = api.sales_tax_amount
    this.salesTaxLabel = api.sales_tax_label
    this.errors = api.errors
    this.status = status
    this.totals = api.totals ?? null
  }

  // A failed request is not loaded, so callers that only branch on `isLoaded`
  // (the legacy Cart) keep their existing "not yet" rendering.
  public get isLoaded(): boolean {
    return this.status === 'loaded'
  }

  public get hasFailed(): boolean {
    return this.status === 'failed'
  }
}
